import React, { useState, useEffect } from "react";
import { FaLongArrowAltLeft } from "react-icons/fa";
import { styles } from "../styles/style";
import PatternLock from "react-pattern-lock";
import { useNavigate } from "react-router-dom";
import {
  useCreateSubadminMutation,
  useGetUniqueIdQuery,
} from "../redux/features/user/userApi";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { toast } from "react-toastify";
import Navbar from "../Components/Navbar";
import PermissionsForm from "../Components/Kyc/PermissionsForm";

const initialPermissions = [
  {
    module: "user_details",
    module_name: "User Details",
    read: false,
    write: false,
    // submodule: [
    //   {
    //     submodule: "personal_info",
    //     submodule_name: "Personal Info",
    //     read: false,
    //     write: false,
    //   },
    //   {
    //     submodule: "security",
    //     submodule_name: "Security",
    //     read: false,
    //     write: false,
    //   },
    //   {
    //     submodule: "wallet",
    //     submodule_name: "Wallet",
    //     read: false,
    //     write: false,
    //   },
    //   {
    //     submodule: "stake_history",
    //     submodule_name: "Stake History",
    //     read: false,
    //     write: false,
    //   },
    //   {
    //     submodule: "transaction_history",
    //     submodule_name: "Transaction History",
    //     read: false,
    //     write: false,
    //   },
    // ],
  },
  {
    module: "stake_history",
    module_name: "Stake History",
    read: false,
    write: false,
  },
  {
    module: "transaction_history",
    module_name: "Transaction History",
    read: false,
    write: false,
  },
  {
    module: "roi_history",
    module_name: "ROI History",
    read: false,
    write: false,
  },
  {
    module: "referal",
    module_name: "Referal",
    read: false,
    write: false,
  },
  {
    module: "report",
    module_name: "Report",
    read: false,
    write: false,
  },
  // {
  //   module: "admin_banks",
  //   module_name: "Admin Banks",
  //   read: false,
  //   write: false,
  // },
];

const AddSubAdmin = () => {
  const [path, setPath] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [confirmPath, setConfirmPath] = useState([]);
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [permissions, setPermissions] = useState(initialPermissions);
  const navigate = useNavigate();
  const [patternError, setPatternError] = useState("");
  const [confirmPatternError, setConfirmPatternError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  const [createSubadmin, { isSuccess, isError, error, data }] = useCreateSubadminMutation();
  const { data: uniqueId, isSuccess: uniqueIdSuccess } = useGetUniqueIdQuery();

  const returnPattern = (path) => {
    if (!path || !Array.isArray(path) || path.length === 0) return "";
    
    let newPath = "";
    for (let x of path) {
      const num = parseInt(x);
      if (!isNaN(num)) {
        newPath += (num + 1).toString();
      }
    }
    return newPath;
  };

  useEffect(() => {
    if (isSuccess && data?.status === true) {
      toast.success(data.message);
      navigate("/subadmin");
    } else if (isSuccess && data?.status !== true) {
      toast.error(data?.message || "Failed to create subadmin");
    }
  }, [isSuccess, data, navigate]);

  const schema = Yup.object().shape({
    name: Yup.string()
      .required("Please enter a username!")
      .matches(/^[a-zA-Z]+$/, "Username can only contain letters")
      .min(3, "Username must be at least 3 characters"),
    email: Yup.string()
      .email("Invalid email!")
      .required("Please enter an email!"),
    password: Yup.string()
      .required("Please enter the password!")
      .min(6, "Password must be at least 6 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number"
      ),
    confirmPassword: Yup.string()
      .required("Please enter the password again to confirm!")
      .min(6, "Password must be at least 6 characters")
      .oneOf([Yup.ref("password"), null], "Passwords must match!"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      username: "",
      confirmPassword: "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        const pattern = returnPattern(path);
        const confirmPattern = returnPattern(confirmPath);

        if (!pattern || !confirmPattern) {
          toast.error("Please set both pattern and confirm pattern");
          return;
        }

        if (pattern !== confirmPattern) {
          toast.error("Pattern and confirm pattern do not match");
          return;
        }

        const hasPermissions = permissions.some(
          (module) => module.read || module.write || 
          module.submodule?.some((sub) => sub.read || sub.write)
        );

        if (!hasPermissions) {
          toast.error("Please set at least one permission");
          return;
        }

        const myData = {
          permission: permissions,
          password: values.password,
          username: values.name,
          unique_id: uniqueId?.unique_id,
          confirmPassword: values.confirmPassword,
          email: values.email,
          pattern: pattern,
          confirm_pattern: confirmPattern,
        };

        await createSubadmin(myData);
      } catch (error) {
        toast.error("Failed to create subadmin");
      }
    },
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  const handlePatternFinish = () => {
    setDisabled(true);
    setPatternError("");
    setConfirmDisabled(false);
  };

  const handleConfirmPatternFinish = () => {
    const pattern = returnPattern(path);
    const confirmPattern = returnPattern(confirmPath);
    
    if (pattern !== confirmPattern) {
      setConfirmPatternError("Patterns do not match");
      setConfirmDisabled(false);
      setConfirmPath([]);
    } else {
      setConfirmDisabled(true);
      setConfirmPatternError("");
    }
  };

  // Real-time validation functions
  const validateUsername = (value) => {
    if (!value) {
      setUsernameError("Username is required");
      return false;
    }
    if (!/^[a-zA-Z]+$/.test(value)) {
      setUsernameError("Username can only contain letters");
      return false;
    }
    if (value.length < 3) {
      setUsernameError("Username must be at least 3 characters");
      return false;
    }
    setUsernameError("");
    return true;
  };

  const validateEmail = (value) => {
    if (!value) {
      setEmailError("Email is required");
      return false;
    }
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (value) => {
    if (!value) {
      setPasswordError("Password is required");
      return false;
    }
    if (value.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
      setPasswordError("Password must contain at least one uppercase letter, one lowercase letter, and one number");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateConfirmPassword = (value, password) => {
    if (!value) {
      setConfirmPasswordError("Please confirm your password");
      return false;
    }
    if (value !== password) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  };

  // Custom handleChange with real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);

    switch (name) {
      case "name":
        validateUsername(value);
        break;
      case "email":
        validateEmail(value);
        break;
      case "password":
        validatePassword(value);
        validateConfirmPassword(values.confirmPassword, value);
        break;
      case "confirmPassword":
        validateConfirmPassword(value, values.password);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Navbar />
      <div className="m-4 border">
          <div className="flex justify-between px-2">
          <h2 className="font-bold text-xl p-2 text-blue-900">
            SubAdmin Details
          </h2>
          <button
            className="m-2 px-2 font-bold text-md text-blue-900 border flex"
            onClick={() => navigate(-1)}
          >
            <span className="py-2 px-1">
              <FaLongArrowAltLeft />
            </span>
            Back
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex p-4 border-y">
            <div className="w-1/2 pr-2">
              <label className={`${styles.label}`}>User Name</label>
              <input
                className={`${styles.input} ${usernameError ? 'border-red-500' : ''}`}
                type="text"
                id="name"
                name="name"
                onChange={handleInputChange}
                value={values.name}
                onBlur={() => validateUsername(values.name)}
              />
              {usernameError && (
                <span className="text-red-500 text-sm mt-1 block">{usernameError}</span>
              )}
              <br />
              <br />
              <label className={`${styles.label}`}>Unique Id</label>
              <input
                className={`${styles.input}`}
                readOnly
                value={uniqueId?.unique_id || ""}
              />
              <br />
              <br />
              <label className={`${styles.label}`}>Password</label>
              <div className="relative">
                <input
                  className={`${styles.input} ${passwordError ? 'border-red-500' : ''}`}
                  type={!showPassword ? "password" : "text"}
                  id="password"
                  name="password"
                  onChange={handleInputChange}
                  value={values.password}
                  onBlur={() => validatePassword(values.password)}
                />
                {!showPassword ? (
                  <AiOutlineEyeInvisible
                    className="absolute top-2 m-3 right-1 cursor-pointer"
                    size={20}
                    onClick={() => setShowPassword(true)}
                  />
                ) : (
                  <AiOutlineEye
                    className="absolute top-2 m-3 right-1 cursor-pointer"
                    size={20}
                    onClick={() => setShowPassword(false)}
                  />
                )}
              </div>
              {passwordError && (
                <span className="text-red-500 text-sm mt-1 block">{passwordError}</span>
              )}
              <br />
              <br />
              <label className={`${styles.label}`}>Pattern</label>
              {mounted && (
                <PatternLock
                  path={path}
                  width={300}
                  size={3}
                  disabled={disabled}
                  onChange={(newPath) => {
                    if (Array.isArray(newPath)) {
                      setPath(newPath);
                    }
                  }}
                  onFinish={handlePatternFinish}
                  className="bg-gradient-to-r mt-2 from-[#0066ff] to-[#3a63a1]"
                />
              )}
              {patternError && (
                <span className="text-red-500 font-medium">{patternError}</span>
              )}
            </div>
            <div className="w-1/2 pl-2">
              <label className={`${styles.label}`}>Email</label>
              <input
                className={`${styles.input} ${emailError ? 'border-red-500' : ''}`}
                type="email"
                name="email"
                id="email"
                onChange={handleInputChange}
                value={values.email}
                onBlur={() => validateEmail(values.email)}
              />
              {emailError && (
                <span className="text-red-500 text-sm mt-1 block">{emailError}</span>
              )}
              <br />
              <br />
              {values.password ? (
                <>
                  <label className={`${styles.label}`}>Confirm Password</label>
                  <div className="relative">
                    <input
                      className={`${styles.input} ${confirmPasswordError ? 'border-red-500' : ''}`}
                      type={!showConfirmPassword ? "password" : "text"}
                      name="confirmPassword"
                      id="confirmPassword"
                      onChange={handleInputChange}
                      value={values.confirmPassword}
                      onBlur={() => validateConfirmPassword(values.confirmPassword, values.password)}
                    />
                    {!showConfirmPassword ? (
                      <AiOutlineEyeInvisible
                        className="absolute top-2 m-3 right-1 cursor-pointer"
                        size={20}
                        onClick={() => setShowConfirmPassword(true)}
                      />
                    ) : (
                      <AiOutlineEye
                        className="absolute top-2 m-3 right-1 cursor-pointer"
                        size={20}
                        onClick={() => setShowConfirmPassword(false)}
                      />
                    )}
                  </div>
                  {confirmPasswordError && (
                    <span className="text-red-500 text-sm mt-1 block">{confirmPasswordError}</span>
                  )}
                  <br />
                  <br />
                </>
              ) : null}
              <label className={`${styles.label}`}>Confirm Pattern</label>
              {mounted && (
                <PatternLock
                  path={confirmPath}
                  width={300}
                  size={3}
                  disabled={confirmDisabled}
                  onChange={(newPath) => {
                    if (Array.isArray(newPath)) {
                      setConfirmPath(newPath);
                    }
                  }}
                  onFinish={handleConfirmPatternFinish}
                  className="bg-gradient-to-r mt-2 from-[#0066ff] to-[#3a63a1]"
                />
              )}
              {confirmPatternError && (
                <span className="text-red-500 font-medium">{confirmPatternError}</span>
              )}
            </div>
          </div>

          <PermissionsForm permissions={permissions} setPermissions={setPermissions} />
          <div className="flex gap-4 px-6">
            <button className={`${styles.button} !w-fit`} type="submit">
              Submit
            </button>
            <button
              className={`${styles.button} !w-fit !bg-red-500`}
              type="button"
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddSubAdmin;
